import { Component, OnInit, OnDestroy, DestroyRef, NgZone, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { AutoComplete } from 'primeng/autocomplete';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { DatePicker } from 'primeng/datepicker';
import { Checkbox } from 'primeng/checkbox';
import { Tooltip } from 'primeng/tooltip';
import { Tag } from 'primeng/tag';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { forkJoin, of, catchError } from 'rxjs';

import { PresalesService } from '../../services/presales.service';
import { EmployeeService } from '../../services/employee.service';
import { RoleService } from '../../services/role.service';
import { ServiceGroupService } from '../../services/service-group.service';
import { ClientService } from '../../services/client.service';

import {
  PresalesClient,
  PresalesCostCenter,
  CreatePresalesClient,
  PresalesProject,
  CreatePresalesProject,
  PromoteToDeliveryRequest,
  SalesStage,
  PresalesProgress,
  Currency,
  CurrencyRate,
  SALES_STAGES,
  PRESALES_PROGRESS_OPTIONS,
  presalesProgressLabel,
  CURRENCIES,
  LEAD_SOURCES,
  buildRateMap,
  toUsd,
} from '../../models/presales.model';
import { Employee } from '../../models/employee.model';
import { Role } from '../../models/role.model';
import { ServiceGroup } from '../../models/service-group.model';
import { Client } from '../../models/client.model';

import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { AuthService } from '../../services/auth.service';

interface Option<T = string> { label: string; value: T; }

/**
 * Groups current projects by progress state — used by the Kanban view.
 * `key` is '' for the catch-all column holding deals with no progress set.
 */
interface KanbanColumn {
  key: PresalesProgress | '';
  label: string;
  cssClass: string;   // progress-to-start / progress-inprogress / …
  projects: PresalesProject[];
}

@Component({
  selector: 'app-presales-project-inventory',
  standalone: true,
  imports: [
    CommonModule, FormsModule, TableModule, Button, Dialog, Select, AutoComplete,
    InputText, InputNumber, DatePicker, Checkbox, Tooltip, Tag, ConfirmDialog
  ],
  providers: [ConfirmationService, DatePipe],
  templateUrl: './presales-project-inventory.component.html',
  styleUrl: './presales-project-inventory.component.scss'
})
export class PresalesProjectInventoryComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  // ================= Data =================
  projects: PresalesProject[] = [];
  filteredProjects: PresalesProject[] = [];
  clients: PresalesClient[] = [];
  // Type-or-select Presales Client field: `clientSearchText` holds whatever is in
  // the input (an existing name, or a new one the user is typing).
  // `selectedClient` is set only when a suggestion is picked.
  filteredClients: PresalesClient[] = [];
  clientSearchText = '';
  selectedClient: PresalesClient | null = null;
  employees: Employee[] = [];
  roles: Role[] = [];
  serviceGroups: ServiceGroup[] = [];
  /** All presales cost centers (pursuit-only table, NOT the main CostCenters). */
  costCenters: PresalesCostCenter[] = [];
  /** Narrowed to the selected client + shared ones — what the form dropdown binds to. */
  costCentersForClient: PresalesCostCenter[] = [];

  loading = true;
  canManage = false;

  /** USD conversion rates (currency → USD-per-unit). Empty until loaded. */
  rateMap = new Map<Currency, number>();

  // Dropdown-ready lists (built after employee/role load — Presales PMs & Account Mgrs)
  projectManagers: Employee[] = [];
  accountManagers: Employee[] = [];

  // ================= Filters =================
  searchText = '';
  selectedStatus: 'active' | 'inactive' | null = null;
  selectedStage: SalesStage | null = null;
  selectedProgress: PresalesProgress | null = null;
  selectedPMId: string | null = null;
  selectedClientId: string | null = null;
  selectedAccountManagerId: string | null = null;
  selectedServiceGroupId: string | null = null;

  statusFilterOptions: Option[] = [
    { label: 'Active',   value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ];

  // View mode: table (default) or kanban
  viewMode: 'table' | 'kanban' = 'table';

  // ================= Options =================
  stageOptions: Option<SalesStage>[] = SALES_STAGES;
  progressOptions: Option<PresalesProgress>[] = PRESALES_PROGRESS_OPTIONS;

  /** Spaces 'InProgress' for display; stored values are left untouched. */
  progressLabel = presalesProgressLabel;
  currencyOptions: Option<Currency>[] = CURRENCIES;
  leadSourceOptions: Option<string>[] = LEAD_SOURCES.map(s => ({ label: s, value: s }));

  // ================= Add / Edit dialog =================
  displayDialog = false;
  isEditMode = false;
  selectedProject: PresalesProject = this.emptyProject();
  fieldTouched: Record<string, boolean> = {};
  /** Snapshot of the stage at the moment the edit dialog opens — lets Save detect a Won/Lost transition. */
  private stageAtOpen: SalesStage | null = null;
  /** Inline error shown under the Project Code field (e.g. duplicate-code from backend). */
  projectCodeError: string | null = null;
  /** True while an Add or Update request is in flight — blocks Save button to prevent double-submit. */
  saveInFlight = false;

  // Reactive Date bindings — PrimeNG DatePicker expects Date objects, not ISO strings
  formStartDate: Date | null = null;
  formEndDate: Date | null = null;
  formExpectedClose: Date | null = null;

  // ================= Kanban =================
  kanbanColumns: KanbanColumn[] = [];

  /**
   * Columns rendered on the board. Every progress state is always shown: an
   * empty "On Hold" column is itself a finding. Mirrors the Details board.
   */
  get visibleKanbanColumns(): KanbanColumn[] {
    return this.kanbanColumns;
  }

  // ================= Promote-to-Delivery dialog =================
  /**
   * Master switch for the Promote-to-Delivery feature in the UI.
   *
   * Turned OFF: promoting stamps the delivery cost center onto the twin Project, and the
   * allocation export then reads that stamped value — so promoted pursuits showed a cost
   * center that no longer matched the presales record. Until that is resolved, promotion is
   * not offered.
   *
   * The wizard, its handlers and the backend endpoint are all left intact; flipping this to
   * true restores the feature. Display of PAST promotions (the verified badge, the
   * "Promoted" column, the toggle-disable rule) is unaffected and stays visible.
   */
  readonly promoteEnabled = false;

  displayPromoteDialog = false;
  promoteTarget: PresalesProject | null = null;
  promoteMode: 'create' | 'link' = 'create';
  promoteExistingClientId: string | null = null;
  promoteProjectNameOverride = '';
  promoteProjectCodeOverride = '';
  promoteInFlight = false;
  mainClients: Client[] = [];
  /** Wizard current step (1 = choose client, 2 = project details, 3 = review). */
  promoteStep: 1 | 2 | 3 = 1;
  /** Static labels shown by the wizard's step indicator. */
  readonly promoteSteps: { n: 1 | 2 | 3; title: string }[] = [
    { n: 1, title: 'Client' },
    { n: 2, title: 'Details' },
    { n: 3, title: 'Review' },
  ];

  constructor(
    private presalesService: PresalesService,
    private employeeService: EmployeeService,
    private roleService: RoleService,
    private serviceGroupService: ServiceGroupService,
    private clientService: ClientService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    public pageHeaderService: PageHeaderSearchActionService,
    private datePipe: DatePipe,
    private ngZone: NgZone,
    private route: ActivatedRoute
  ) {
    // Handoff from the Details dialog's "Open in Inventory":
    // /presales/inventory?projectId=…&openForm=1. Read in the constructor so the flags are
    // set before loadAll() resolves and tries to consume them.
    const qp = this.route.snapshot.queryParamMap;
    this.pendingProjectId = qp.get('projectId');
    this.pendingOpenForm = qp.get('openForm') === '1';
  }

  /** Set by the ?projectId=…&openForm=1 handoff; consumed once after the first load. */
  private pendingProjectId: string | null = null;
  private pendingOpenForm = false;

  /**
   * Completes the handoff: opens the edit dialog on the requested project.
   *
   * Deferred until the projects have loaded, because the dialog is populated from the
   * loaded row rather than re-fetched. Consumed once and cleared either way, so a later
   * filter change cannot reopen a stale project.
   */
  private consumePendingProjectHandoff(): void {
    if (!this.pendingOpenForm || !this.pendingProjectId) return;

    const projectId = this.pendingProjectId;
    this.pendingOpenForm = false;
    this.pendingProjectId = null;

    // Editing is gated the same way the list's own rows are: a viewer who may not manage
    // projects lands on the list rather than in a form they cannot save.
    if (!this.canManage) return;

    const project = this.projects.find(p => p.id === projectId);
    if (!project) {
      // Deleted or deactivated between the two screens. Saying so beats opening nothing
      // and looking like a dead click.
      this.messageService.add({
        severity: 'warn',
        summary: 'Project unavailable',
        detail: 'That presales project could not be found.'
      });
      return;
    }

    this.showEditDialog(project);
  }

  ngOnInit(): void {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search projects, clients, PMs…');
    this.pageHeaderService.setPrimaryButtonLabel('Add Presales Project');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(true);

    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.canManage = !!(user?.isAdmin || user?.isPM);
      this.pageHeaderService.setCanShowPrimaryAction(this.canManage);
    });

    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(v => { this.searchText = v ?? ''; this.applyFilters(); });

    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.showAddDialog());

    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.ngZone.run(() => this.exportToExcel()));

    this.loadAll();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  // ================= Loading =================

  loadAll(): void {
    this.loading = true;

    forkJoin({
      projects:      this.presalesService.getAllProjects().pipe(catchError(() => of([] as PresalesProject[]))),
      clients:       this.presalesService.getAllClients().pipe(catchError(() => of([] as PresalesClient[]))),
      employees:     this.employeeService.getAllEmployees().pipe(catchError(() => of([] as Employee[]))),
      roles:         this.roleService.getAllRoles().pipe(catchError(() => of([] as Role[]))),
      serviceGroups: this.serviceGroupService.getAllServiceGroups().pipe(catchError(() => of([] as ServiceGroup[]))),
      costCenters:   this.presalesService.getAllCostCenters().pipe(catchError(() => of([] as PresalesCostCenter[]))),
      rates:         this.presalesService.getCurrencyRates().pipe(catchError(() => of([] as CurrencyRate[])))
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.projects = data.projects || [];
        this.rateMap = buildRateMap(data.rates);
        this.clients  = (data.clients || []).filter(c => c.isActive);
        this.employees = (data.employees || []).filter(e => e.isActive);
        this.roles = (data.roles || []).filter(r => r.isActive);
        this.serviceGroups = (data.serviceGroups || []).filter(sg => sg.isActive);
        this.costCenters   = (data.costCenters || []).filter(cc => cc.isActive);
        this.refreshCostCentersForClient();

        // Filter employees to Project Managers and Account Managers by role (same pattern as ProjectManagement)
        const pmRole = this.roles.find(r =>
          r.title?.toLowerCase().includes('project manager') || r.title?.toLowerCase() === 'pm'
        );
        this.projectManagers = pmRole
          ? this.employees.filter(e =>
              (e.roleIds && e.roleIds.includes(pmRole.id)) || e.roleId === pmRole.id
            )
          : [];

        const amRole = this.roles.find(r => r.title?.toLowerCase() === 'account manager');
        this.accountManagers = amRole
          ? this.employees.filter(e =>
              (e.roleIds && e.roleIds.includes(amRole.id)) || e.roleId === amRole.id
            )
          : [];

        this.applyFilters();
        this.loading = false;

        // Deferred until here: the edit dialog is populated from the loaded row, and its
        // client / cost-center dropdowns need the lists beside it.
        this.consumePendingProjectHandoff();
      },
      error: () => {
        this.loading = false;
        // Drop the handoff rather than leaving it armed: the projects never loaded, so a
        // later successful load must not pop a dialog the user has stopped expecting.
        this.pendingOpenForm = false;
        this.pendingProjectId = null;
      }
    });
  }

  // ================= Filters =================

  applyFilters(): void {
    let list = [...this.projects];
    const s = this.searchText.trim().toLowerCase();
    if (s) {
      list = list.filter(p =>
        p.name?.toLowerCase().includes(s) ||
        p.projectCode?.toLowerCase().includes(s) ||
        p.presalesClientName?.toLowerCase().includes(s) ||
        p.managerName?.toLowerCase().includes(s) ||
        p.accountManagerName?.toLowerCase().includes(s)
      );
    }
    if (this.selectedStatus === 'active') {
      list = list.filter(p => p.isActive === true);
    } else if (this.selectedStatus === 'inactive') {
      list = list.filter(p => p.isActive === false);
    }
    if (this.selectedStage) {
      list = list.filter(p => p.salesStage === this.selectedStage);
    }
    if (this.selectedProgress) {
      list = list.filter(p => p.progress === this.selectedProgress);
    }
    if (this.selectedPMId) {
      list = list.filter(p => p.managerId === this.selectedPMId);
    }
    if (this.selectedClientId) {
      list = list.filter(p => p.presalesClientId === this.selectedClientId);
    }
    if (this.selectedAccountManagerId) {
      list = list.filter(p => p.accountManagerId === this.selectedAccountManagerId);
    }
    if (this.selectedServiceGroupId) {
      list = list.filter(p => p.serviceGroupId === this.selectedServiceGroupId);
    }
    this.filteredProjects = list;
    this.rebuildKanbanColumns();
    this.pageHeaderService.setExportDisabled(this.filteredProjects.length === 0);
  }

  clearFilters(): void {
    this.selectedStatus = null;
    this.selectedStage = null;
    this.selectedProgress = null;
    this.selectedPMId = null;
    this.selectedClientId = null;
    this.selectedAccountManagerId = null;
    this.selectedServiceGroupId = null;
    // Also clear the header search box — otherwise "Clear filters" is a partial
    // reset from the user's point of view.
    this.pageHeaderService.setSearchText('');
    this.applyFilters();
  }

  hasActiveFilters(): boolean {
    return !!(
      this.selectedStatus ||
      this.selectedStage ||
      this.selectedProgress ||
      this.selectedPMId ||
      this.selectedClientId ||
      this.selectedAccountManagerId ||
      this.selectedServiceGroupId
    );
  }

  // ================= Stats =================
  //
  // KPI cards show ONE currency (USD) so the numbers are comparable at a glance.
  // Each deal is converted from its native currency via the cached FX rates.
  // The table rows below still show each deal's original currency — those are
  // the real contracted amounts. Deals whose currency has no known rate are
  // excluded from the USD total and counted in `unratedDealCount` so the figure
  // is never silently understated.

  /** Open deals (not Won/Lost). Shared by both pipeline getters. */
  private get openProjects(): PresalesProject[] {
    return this.projects.filter(p => p.salesStage !== 'Won' && p.salesStage !== 'Lost');
  }

  /** Active pipeline value in USD = Σ convert(RevenueTarget). */
  get usdPipeline(): number {
    let total = 0;
    for (const p of this.openProjects) {
      const usd = toUsd(p.revenueTarget, p.currency, this.rateMap);
      if (usd != null) total += usd;
    }
    return Math.round(total);
  }

  /** Weighted pipeline in USD = Σ convert(RevenueTarget × WinningPercentage / 100). */
  get usdWeighted(): number {
    let total = 0;
    for (const p of this.openProjects) {
      const usd = toUsd((p.revenueTarget ?? 0) * (p.winningPercentage ?? 0) / 100, p.currency, this.rateMap);
      if (usd != null) total += usd;
    }
    return Math.round(total);
  }

  /** Count of open deals whose currency has no rate (so the USD total omits them). */
  get unratedDealCount(): number {
    if (this.rateMap.size === 0) return 0;
    return this.openProjects.filter(p => !this.rateMap.has(p.currency)).length;
  }

  /** True once at least one rate is loaded — gates the USD display vs. a dash. */
  get hasRates(): boolean { return this.rateMap.size > 0; }

  /** Average open-deal size in USD = usdPipeline / number of open deals with a known rate. */
  get usdAvgDealSize(): number {
    const rated = this.openProjects.filter(p => this.rateMap.has(p.currency)).length;
    if (rated === 0) return 0;
    return Math.round(this.usdPipeline / rated);
  }

  /** Open deals whose Expected Close date falls in the current calendar month. */
  get closingThisMonthCount(): number {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    return this.openProjects.filter(p => {
      if (!p.expectedCloseDate) return false;
      const d = new Date(p.expectedCloseDate);
      return d.getFullYear() === y && d.getMonth() === m;
    }).length;
  }

  get wonCount(): number { return this.projects.filter(p => p.salesStage === 'Won').length; }
  get lostCount(): number { return this.projects.filter(p => p.salesStage === 'Lost').length; }

  get winRatePct(): number {
    const closed = this.wonCount + this.lostCount;
    if (closed === 0) return 0;
    return Math.round((this.wonCount / closed) * 100);
  }

  // ================= View mode =================

  setViewMode(mode: 'table' | 'kanban'): void {
    this.viewMode = mode;
    if (mode === 'kanban') this.rebuildKanbanColumns();
  }

  /**
   * Buckets the filtered projects into one column per progress state — the
   * working status a PM moves a pursuit through. Sales stage is shown on the
   * card instead (coloured edge + badge), so both readings stay available.
   * Mirrors rebuildKanbanColumns in PresalesProjectDetailsComponent so the two
   * boards group, order and colour identically.
   */
  private rebuildKanbanColumns(): void {
    const columns: KanbanColumn[] = PRESALES_PROGRESS_OPTIONS.map(o => ({
      key: o.value,
      label: o.label,
      cssClass: 'progress-' + o.value.toLowerCase().replace(/\s+/g, '-'),
      projects: []
    }));
    const unset: KanbanColumn = { key: '', label: 'Not Set', cssClass: 'progress-unset', projects: [] };

    for (const p of this.filteredProjects) {
      const col = columns.find(c => c.key === p.progress) ?? unset;
      col.projects.push(p);
    }

    // Biggest deals first, by USD where a rate exists; unrated sort last rather
    // than being treated as zero.
    const weight = (p: PresalesProject) => toUsd(p.revenueTarget ?? 0, p.currency, this.rateMap) ?? -1;
    for (const c of columns) c.projects.sort((a, b) => weight(b) - weight(a));
    unset.projects.sort((a, b) => weight(b) - weight(a));

    this.kanbanColumns = unset.projects.length ? [...columns, unset] : columns;
  }

  /** Total USD value of a column, for the header subtotal. Null when no rates loaded. */
  columnUsdTotal(col: KanbanColumn): number | null {
    if (!this.hasRates) return null;
    let total = 0;
    for (const p of col.projects) {
      const usd = toUsd(p.revenueTarget ?? 0, p.currency, this.rateMap);
      if (usd != null) total += usd;
    }
    return Math.round(total);
  }

  // ---- Kanban card helpers (mirrored from PresalesProjectDetailsComponent) ----

  /** Stage class for a card — the columns group by progress, so the edge carries stage. */
  stageClass(stage?: SalesStage | null): string {
    return 'stage-' + (stage || 'unset').toLowerCase().replace(/\s+/g, '-');
  }

  /** Pill class for the RAG status badge. */
  projectStatusBadgeClass(status?: string | null): string {
    switch ((status || '').toLowerCase()) {
      case 'green': return 'status-badge-green';
      case 'amber': return 'status-badge-amber';
      case 'red':   return 'status-badge-red';
      default:      return 'status-badge-default';
    }
  }

  /**
   * Abbreviated money for cards — "1.2M" / "450K". A card shares its value row
   * with the win bar, so a full LKR figure would wrap. Exact value is in the dialog.
   */
  formatMoneyCompact(n: number): string {
    const v = n ?? 0;
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return (v / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1).replace(/\.0$/, '') + 'M';
    if (abs >= 1_000)     return (v / 1_000).toFixed(abs >= 10_000 ? 0 : 1).replace(/\.0$/, '') + 'K';
    return v.toLocaleString('en-US');
  }

  /** Up to two initials for a card avatar; '?' when the name is missing. */
  initials(name?: string | null): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  }

  /** Deterministic avatar tint from a name, so a person keeps one colour. */
  avatarHue(name?: string | null): number {
    const s = name || '';
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
    return [210, 150, 275, 25, 340, 190][h % 6];
  }

  /**
   * Whole days past the end date, or null when unset / not yet due. The
   * Inventory table has no Overdue column, but the board flags it on the card.
   */
  calcOverdueDays(endDate?: string | null): number | null {
    if (!endDate) return null;
    const end = new Date(endDate);
    if (isNaN(end.getTime())) return null;
    end.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.floor((today.getTime() - end.getTime()) / 86400000);
    return days > 0 ? days : null;
  }

  /** Days until expected close — negative once past, null when unset. */
  private daysToClose(iso?: string | null): number | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((d.getTime() - today.getTime()) / 86400000);
  }

  /** "Closes in 5d" / "Closes today", falling back to the date when far out. */
  closeLabel(iso?: string | null): string {
    const d = this.daysToClose(iso);
    if (d == null) return '';
    if (d === 0) return 'Closes today';
    if (d > 0)   return d > 60 ? this.formatDate(iso) : `Closes in ${d}d`;
    return this.formatDate(iso);
  }

  /** True when expected close is inside the next 14 days. */
  isClosingSoon(iso?: string | null): boolean {
    const d = this.daysToClose(iso);
    return d != null && d >= 0 && d <= 14;
  }

  // ================= Add / Edit dialog =================

  showAddDialog(): void {
    this.selectedProject = this.emptyProject();
    this.formStartDate = new Date();
    this.formEndDate = null;
    this.formExpectedClose = null;
    this.isEditMode = false;
    this.stageAtOpen = null;
    this.fieldTouched = {};
    this.projectCodeError = null;
    this.saveInFlight = false;
    this.clientSearchText = '';
    this.selectedClient = null;
    this.filteredClients = [...this.clients];
    this.refreshCostCentersForClient();
    this.reloadCostCenters();
    this.displayDialog = true;
  }

  showEditDialog(project: PresalesProject): void {
    this.selectedProject = { ...project };
    this.formStartDate = project.startDate ? new Date(project.startDate) : new Date();
    this.formEndDate = project.endDate ? new Date(project.endDate) : null;
    this.formExpectedClose = project.expectedCloseDate ? new Date(project.expectedCloseDate) : null;
    this.isEditMode = true;
    this.stageAtOpen = project.salesStage;
    this.fieldTouched = {};
    this.projectCodeError = null;
    this.saveInFlight = false;
    this.selectedClient = this.clients.find(c => c.id === project.presalesClientId) ?? null;
    this.clientSearchText = this.selectedClient?.name ?? '';
    this.filteredClients = [...this.clients];
    this.refreshCostCentersForClient();
    this.reloadCostCenters();
    this.displayDialog = true;
  }

  /**
   * Narrow the cost-center dropdown to the selected client's own cost centers plus the
   * SHARED ones (rows with no client). Filtered client-side from the full list so
   * switching clients doesn't cost a round-trip.
   *
   * If the currently-selected cost center is not valid for the new client, it is cleared
   * rather than left dangling — otherwise the form would submit a cost center the user
   * can no longer see in the list.
   */
  private refreshCostCentersForClient(): void {
    const clientId = this.selectedProject?.presalesClientId ?? null;
    this.costCentersForClient = this.costCenters.filter(
      cc => !cc.presalesClientId || cc.presalesClientId === clientId
    );

    const current = this.selectedProject?.costCenterId;
    if (current && !this.costCentersForClient.some(cc => cc.id === current)) {
      this.selectedProject.costCenterId = null;
      this.selectedProject.costCenterName = '';
    }
  }

  /**
   * Re-fetch the cost-center list, then re-apply the client filter.
   *
   * loadAll() fetches once on component init, so a cost center created afterwards (in the
   * Presales Cost Centers screen, or by someone else) was absent from the dropdown until a
   * full page reload — which reads as "the cost center I just created didn't save".
   * Called on every dialog open; failures leave the cached list in place rather than
   * emptying a dropdown the user is about to use.
   */
  private reloadCostCenters(): void {
    this.presalesService.getAllCostCenters()
      .pipe(
        catchError(() => of(this.costCenters)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(list => {
        this.costCenters = (list || []).filter(cc => cc.isActive);
        this.refreshCostCentersForClient();
      });
  }

  // ---------- Type-or-select Presales Client ----------
  searchClient(event: { query: string }): void {
    const q = (event.query ?? '').toLowerCase().trim();
    this.filteredClients = q
      ? this.clients.filter(c => (c.name ?? '').toLowerCase().includes(q))
      : [...this.clients];
  }

  onClientSelect(event: any): void {
    const client: PresalesClient = event?.value ?? event;
    this.selectedClient = client ?? null;
    this.selectedProject.presalesClientId = client?.id;
    this.clientSearchText = client?.name ?? '';
    this.refreshCostCentersForClient();
  }

  onClientClear(): void {
    this.selectedClient = null;
    this.selectedProject.presalesClientId = undefined;
    this.clientSearchText = '';
    this.refreshCostCentersForClient();
  }

  hideDialog(): void {
    // Never close while a save is in flight — protects against stale writes if
    // the user closes the dialog before the response returns.
    // The internal success/error handlers bypass this check by writing
    // `this.displayDialog = false` directly if they need to (they don't today).
    if (this.saveInFlight) return;
    this.displayDialog = false;
  }

  saveProject(): void {
    // Double-click / concurrent-submit guard.
    if (this.saveInFlight) return;

    this.fieldTouched['name']         = true;
    this.fieldTouched['managerId']    = true;
    this.fieldTouched['costCenterId'] = true;
    if (this.selectedProject.salesStage === 'Lost') {
      this.fieldTouched['lostReason'] = true;
    }

    if (!this.selectedProject.name?.trim())     return;
    if (!this.selectedProject.managerId)         return;
    // Cost center is required: the twin Project copies it so the allocation form can
    // auto-fill, exactly as it does for a normal project.
    if (!this.selectedProject.costCenterId)      return;
    // Lost stage requires a reason — otherwise the deal is closed without any
    // context for the win-loss report. Fail the save until the user fills it in.
    if (this.selectedProject.salesStage === 'Lost' && !this.selectedProject.lostReason?.trim()) return;

    // The Presales Client field is type-or-select: resolve it (reuse an existing
    // client, or create the typed one) BEFORE writing the project, so the project
    // has a real presalesClientId to reference.
    this.resolveClientThenSave();
  }

  /**
   * Resolves the typed/selected Presales Client, then continues the save.
   * Mirrors the delivery-side flow in project-management, but posts to
   * PresalesService.addClient (a *presales* client, not a delivery client).
   */
  private resolveClientThenSave(): void {
    const typed = (this.clientSearchText ?? '').trim();

    // Empty → no client on this project.
    if (!typed) {
      this.selectedProject.presalesClientId = undefined;
      this.commitProjectSave();
      return;
    }

    // Picked from the dropdown.
    if (this.selectedClient && this.selectedClient.name === typed) {
      this.selectedProject.presalesClientId = this.selectedClient.id;
      this.commitProjectSave();
      return;
    }

    // Typed a name that already exists (case-insensitive) → reuse it rather than
    // creating a duplicate.
    const existing = this.clients.find(c => (c.name ?? '').toLowerCase() === typed.toLowerCase());
    if (existing) {
      this.selectedProject.presalesClientId = existing.id;
      this.commitProjectSave();
      return;
    }

    // Genuinely new → create the presales client first.
    this.saveInFlight = true;
    const dto: CreatePresalesClient = { name: typed, isActive: true };

    this.presalesService.addClient(dto)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (created) => {
          this.clients = [...this.clients, created];
          this.filteredClients = [...this.clients];
          this.selectedClient = created;
          this.selectedProject.presalesClientId = created.id;
          this.saveInFlight = false;
          this.commitProjectSave();
        },
        error: () => {
          this.saveInFlight = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to create presales client "${typed}". Please try again.`
          });
        }
      });
  }

  private commitProjectSave(): void {
    // Backend requires StartDate + SalesStage + RevenueTarget as non-null. Since the
    // form now treats them as optional, fall back to sensible defaults.
    const startDate = this.formStartDate ?? new Date();
    const salesStage: SalesStage = this.selectedProject.salesStage || 'Lead';

    const dto: CreatePresalesProject = {
      projectCode:        this.selectedProject.projectCode?.trim() || undefined,
      name:               this.selectedProject.name.trim(),
      managerId:          this.selectedProject.managerId,
      accountManagerId:   this.selectedProject.accountManagerId || undefined,
      presalesClientId:   this.selectedProject.presalesClientId || undefined,
      serviceGroupId:     this.selectedProject.serviceGroupId || undefined,
      costCenterId:       this.selectedProject.costCenterId || undefined,
      revenueTarget:      this.selectedProject.revenueTarget ?? 0,
      currency:           this.selectedProject.currency,
      winningPercentage:  Math.max(0, Math.min(100, this.selectedProject.winningPercentage ?? 0)),
      salesStage:         salesStage,
      progress:           this.selectedProject.progress || undefined,
      // Carried through untouched — not edited on this screen, but the backend assigns them
      // unconditionally, so omitting them would null values set on the Details page.
      projectStatus:      this.selectedProject.projectStatus || undefined,
      plannedEstimateDays: this.selectedProject.plannedEstimateDays ?? undefined,
      description:        this.selectedProject.description || undefined,
      scopeSummary:       this.selectedProject.scopeSummary || undefined,
      teamMembers:        this.selectedProject.teamMembers || undefined,
      comments:           this.selectedProject.comments || undefined,
      leadSource:         this.selectedProject.leadSource || undefined,
      competitor:         this.selectedProject.competitor || undefined,
      lostReason:         salesStage === 'Lost' ? (this.selectedProject.lostReason || undefined) : undefined,
      startDate:          this.toIsoDate(startDate),
      endDate:            this.formEndDate ? this.toIsoDate(this.formEndDate) : undefined,
      expectedCloseDate:  this.formExpectedClose ? this.toIsoDate(this.formExpectedClose) : undefined,
      isActive:           this.selectedProject.isActive
    };

    if (this.isEditMode && this.selectedProject.id) {
      // Confirm before writing when the user is transitioning to Won or Lost from a
      // different stage — those are terminal and stamp ActualCloseDate on the backend.
      const newStage = dto.salesStage;
      const enteringTerminalStage = (newStage === 'Won' || newStage === 'Lost')
        && this.stageAtOpen !== newStage;

      if (enteringTerminalStage) {
        const goingWon = newStage === 'Won';
        // Lock the outer Edit dialog while the confirm is pending — otherwise
        // the user could dismiss it via Escape / mask-click and still trigger
        // a stale update by then clicking Yes on the confirm.
        this.saveInFlight = true;
        this.confirmationService.confirm({
          header: `Mark deal as ${newStage}?`,
          message: `You are about to change stage to ${newStage}. ` +
                   `This will also stamp today as the Actual Close Date.`,
          icon: goingWon ? 'pi pi-check-circle' : 'pi pi-times-circle',
          acceptLabel: `Yes, save as ${newStage}`,
          rejectLabel: 'Cancel',
          acceptButtonStyleClass: goingWon ? 'p-button-success' : 'p-button-danger',
          rejectButtonStyleClass: 'p-button-text',
          accept: () => {
            // persistUpdate manages saveInFlight itself, so reset ours first so
            // its own set/clear doesn't collide with our lock.
            this.saveInFlight = false;
            this.persistUpdate(dto, /*offerPromote*/ goingWon);
          },
          reject: () => {
            // User cancelled — unlock the dialog so they can edit or retry.
            this.saveInFlight = false;
          },
        });
        return;
      }

      this.persistUpdate(dto, /*offerPromote*/ false);
    } else {
      this.saveInFlight = true;
      this.presalesService.addProject(dto)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (created) => {
            this.saveInFlight = false;
            this.projects = [created, ...this.projects];
            this.applyFilters();
            this.messageService.add({ severity: 'success', summary: 'Created', detail: `"${created.name}" added` });
            this.hideDialog();
          },
          error: (err) => {
            this.saveInFlight = false;
            this.handleSaveError(err, 'Failed to add presales project');
          }
        });
    }
  }

  /**
   * Sends save errors to the right surface:
   *   - Duplicate-code errors → inline red text under the Project Code field
   *     (much clearer UX than a toast that doesn't say WHICH field is wrong).
   *   - Everything else → toast, as before.
   */
  private handleSaveError(err: unknown, fallback: string): void {
    const message = this.extractApiError(err) || fallback;
    // Backend surfaces duplicate-code as: 'Presales project code "PS-001" is already used by "…"'
    // Match on a few reliable substrings so we don't miss variations.
    const lower = message.toLowerCase();
    const isDupCode = lower.includes('project code') && (lower.includes('already used') || lower.includes('already exists'));
    if (isDupCode) {
      this.projectCodeError = message;
      return;
    }
    this.messageService.add({ severity: 'error', summary: 'Could not save', detail: message });
  }

  /**
   * Writes the DTO to the API in edit mode. If offerPromote is true and the saved
   * project isn't already promoted, prompts the user to promote-to-delivery now.
   */
  private persistUpdate(dto: CreatePresalesProject, offerPromote: boolean): void {
    if (!this.isEditMode || !this.selectedProject.id) return;

    this.saveInFlight = true;
    this.presalesService.updateProject(this.selectedProject.id, dto)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          this.saveInFlight = false;
          const idx = this.projects.findIndex(p => p.id === saved.id);
          if (idx >= 0) this.projects[idx] = saved;
          this.applyFilters();
          // Refresh the baseline so a follow-up Save within the same open dialog
          // compares against the latest persisted state, not the pre-open value.
          this.stageAtOpen = saved.salesStage;
          this.selectedProject = { ...saved };
          this.messageService.add({ severity: 'success', summary: 'Updated', detail: `"${saved.name}" saved` });
          this.hideDialog();

          if (this.promoteEnabled && offerPromote && !saved.promotedToProjectId) {
            this.confirmationService.confirm({
              header: 'Promote to Delivery now?',
              message: `"${saved.name}" is now Won. Would you like to promote it to delivery immediately?`,
              icon: 'pi pi-send',
              acceptLabel: 'Yes, promote now',
              rejectLabel: 'Promote later',
              acceptButtonStyleClass: 'p-button-success',
              rejectButtonStyleClass: 'p-button-text',
              accept: () => this.openPromoteDialog(saved),
            });
          }
        },
        error: (err) => {
          this.saveInFlight = false;
          this.handleSaveError(err, 'Failed to update presales project');
        }
      });
  }

  /**
   * Pulls the readable API error message out of an HttpErrorResponse.
   * The backend's GlobalExceptionHandlerMiddleware wraps BadRequestException as:
   *   { statusCode, message: "Invalid request", details: "<the real reason>" }
   * so we prefer `details` (the specific, actionable message) over `message`
   * (the generic label).
   */
  private extractApiError(err: unknown): string | null {
    const anyErr = err as { error?: unknown; message?: string };
    if (anyErr?.error) {
      if (typeof anyErr.error === 'string') return anyErr.error;
      const body = anyErr.error as { message?: string; title?: string; detail?: string; details?: string };
      return body.details || body.detail || body.message || body.title || null;
    }
    return anyErr?.message ?? null;
  }

  /** Builds a CreatePresalesProject DTO from a loaded PresalesProject — used by toggleActive(). */
  private toCreateDto(project: PresalesProject, overrides: Partial<CreatePresalesProject> = {}): CreatePresalesProject {
    return {
      projectCode:        project.projectCode?.trim() || undefined,
      name:               project.name,
      managerId:          project.managerId,
      accountManagerId:   project.accountManagerId || undefined,
      presalesClientId:   project.presalesClientId || undefined,
      serviceGroupId:     project.serviceGroupId || undefined,
      costCenterId:       project.costCenterId || undefined,
      revenueTarget:      project.revenueTarget,
      currency:           project.currency,
      winningPercentage:  project.winningPercentage,
      salesStage:         project.salesStage,
      progress:           project.progress || undefined,
      // Carried through untouched — see commitProjectSave(); a bare active/inactive toggle
      // must not wipe the planned estimate or the RAG status.
      projectStatus:      project.projectStatus || undefined,
      plannedEstimateDays: project.plannedEstimateDays ?? undefined,
      description:        project.description || undefined,
      scopeSummary:       project.scopeSummary || undefined,
      teamMembers:        project.teamMembers || undefined,
      comments:           project.comments || undefined,
      leadSource:         project.leadSource || undefined,
      competitor:         project.competitor || undefined,
      lostReason:         project.lostReason || undefined,
      startDate:          project.startDate,
      endDate:            project.endDate || undefined,
      expectedCloseDate:  project.expectedCloseDate || undefined,
      isActive:           project.isActive,
      ...overrides,
    };
  }

  /**
   * Flip the project's active state via the standard update endpoint. Same pattern as
   * client-management.toggleActiveStatus — no confirm dialog, one round-trip.
   *
   * The old `if (project.promotedToProjectId) return;` guard is gone: that column is stamped
   * at pursuit CREATION, so it matched every row and silently swallowed every toggle.
   */
  toggleActive(project: PresalesProject): void {
    const activating = !project.isActive;
    this.confirmationService.confirm({
      message: `Are you sure you want to ${activating ? 'activate' : 'deactivate'} "${project.name}"?`,
      header: activating ? 'Confirm Activate' : 'Confirm Deactivate',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: activating ? 'p-button-success' : 'p-button-danger',
      accept: () => {
    const dto = this.toCreateDto(project, { isActive: !project.isActive });
    this.presalesService.updateProject(project.id, dto)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          const idx = this.projects.findIndex(p => p.id === saved.id);
          if (idx >= 0) this.projects[idx] = saved;
          this.applyFilters();
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Presales project ${saved.isActive ? 'activated' : 'deactivated'} successfully`
          });
        },
        error: (err) => this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.extractApiError(err) || 'Failed to update project status'
        })
      });
      }
    });
  }

  // ================= Promote-to-Delivery =================

  openPromoteDialog(project: PresalesProject): void {
    if (project.promotedToProjectId) {
      this.messageService.add({
        severity: 'warn', summary: 'Already promoted',
        detail: 'This presales project has already been promoted to delivery.'
      });
      return;
    }
    if (project.salesStage !== 'Won') {
      this.messageService.add({
        severity: 'warn', summary: 'Cannot promote',
        detail: 'Only Won presales projects can be promoted to delivery.'
      });
      return;
    }

    this.promoteTarget = project;
    // Default to "create new client" when a presales client exists, otherwise force link mode.
    this.promoteMode = project.presalesClientName ? 'create' : 'link';
    this.promoteExistingClientId = null;
    // Pre-fill delivery name with the presales project name — most of the time
    // the user wants to keep the same name, and this saves them a copy-paste.
    // Editable — they can override to something like "AIA Insurance — Wave 1".
    this.promoteProjectNameOverride = project.name;
    this.promoteProjectCodeOverride = '';
    this.promoteInFlight = false;
    this.promoteStep = 1;
    this.displayPromoteDialog = true;

    // Refresh main Clients every time — otherwise newly-added clients created
    // between opens (in another tab or by another user) would be invisible in
    // the "Link existing" dropdown.
    this.clientService.getAllClients()
      .pipe(takeUntilDestroyed(this.destroyRef), catchError(() => of([] as Client[])))
      .subscribe(list => {
        this.mainClients = (list || []).filter(c => c.isActive);
      });
  }

  hidePromoteDialog(): void {
    if (this.promoteInFlight) return;
    this.displayPromoteDialog = false;
    this.promoteTarget = null;
  }

  /** Used by the Promote dialog's Review step to name the linked main-Clients row. */
  getMainClientName(clientId: string | null): string {
    if (!clientId) return '—';
    return this.mainClients.find(c => c.id === clientId)?.name ?? '—';
  }

  canConfirmPromote(): boolean {
    if (!this.promoteTarget) return false;
    if (this.promoteInFlight) return false;
    if (this.promoteMode === 'link' && !this.promoteExistingClientId) return false;
    if (this.promoteMode === 'create' && !this.promoteTarget.presalesClientName) return false;
    return true;
  }

  /**
   * True when the current wizard step's inputs are valid and the user can advance.
   * Step 1 requires a valid client choice; step 2 has no required inputs; step 3
   * is the review step and uses the full canConfirmPromote() check.
   */
  canGoNextPromoteStep(): boolean {
    if (!this.promoteTarget) return false;
    if (this.promoteStep === 1) {
      if (this.promoteMode === 'create') return !!this.promoteTarget.presalesClientName;
      if (this.promoteMode === 'link')   return !!this.promoteExistingClientId;
    }
    return true;
  }

  nextPromoteStep(): void {
    if (!this.canGoNextPromoteStep()) return;
    if (this.promoteStep < 3) {
      this.promoteStep = (this.promoteStep + 1) as 1 | 2 | 3;
    }
  }

  backPromoteStep(): void {
    if (this.promoteStep > 1) {
      this.promoteStep = (this.promoteStep - 1) as 1 | 2 | 3;
    }
  }

  confirmPromote(): void {
    if (!this.promoteTarget || !this.canConfirmPromote()) return;

    const req: PromoteToDeliveryRequest = {
      createNewClient:      this.promoteMode === 'create',
      existingClientId:     this.promoteMode === 'link' ? this.promoteExistingClientId : null,
      deliveryProjectName:  this.promoteProjectNameOverride.trim() || undefined,
      deliveryProjectCode:  this.promoteProjectCodeOverride.trim() || undefined,
    };

    this.promoteInFlight = true;
    const targetId = this.promoteTarget.id;
    const targetName = this.promoteTarget.name;

    this.presalesService.promoteProject(targetId, req)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.promoteInFlight = false;
          this.displayPromoteDialog = false;

          // Reflect the newly-stamped promotedToProjectId locally so the row updates
          // without needing a full reload.
          const idx = this.projects.findIndex(p => p.id === targetId);
          if (idx >= 0) {
            this.projects[idx] = { ...this.projects[idx], promotedToProjectId: result.promotedToProjectId };
          }
          this.applyFilters();
          this.messageService.add({
            severity: 'success',
            summary: 'Promoted to Delivery',
            detail: result.message || `"${targetName}" moved to active delivery.`
          });
          this.promoteTarget = null;
        },
        error: (err) => {
          this.promoteInFlight = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Promote failed',
            detail: this.extractApiError(err) || 'Failed to promote presales project.'
          });
        }
      });
  }


  // ================= Row helpers =================

  /** Bootstrap-like severity for the stage chip. */
  stageSeverity(stage: SalesStage): 'info' | 'success' | 'warn' | 'secondary' | 'danger' {
    switch (stage) {
      case 'Won':         return 'success';
      case 'Lost':        return 'danger';
      case 'Negotiation': return 'warn';
      case 'Proposal':    return 'info';
      case 'Qualified':   return 'info';
      case 'Lead':        return 'secondary';
      default:            return 'secondary';
    }
  }

  /**
   * Severity for the pursuit-progress chip. The 'on track' / 'off track' / 'blocker'
   * cases are retired from the dropdown but kept so legacy rows still read in colour.
   */
  progressSeverity(progress?: PresalesProgress | string | null): 'info' | 'success' | 'warn' | 'secondary' | 'danger' {
    switch ((progress || '').toLowerCase()) {
      case 'inprogress': return 'info';
      case 'closed':     return 'success';
      case 'on hold':    return 'danger';
      case 'to start':   return 'secondary';
      case 'on track':   return 'success';
      case 'off track':  return 'warn';
      case 'blocker':    return 'danger';
      default:           return 'secondary';
    }
  }

  formatDate(iso?: string | null): string {
    if (!iso) return '—';
    return this.datePipe.transform(iso, 'MMM d, y') ?? '—';
  }

  formatMoney(n: number): string {
    return (n ?? 0).toLocaleString('en-US');
  }

  // ================= Excel Export =================

  exportToExcel(): void {
    if (!this.filteredProjects.length) return;
    const rows = this.filteredProjects.map(p => ({
      'Code':              p.projectCode || '',
      'Project Name':      p.name,
      'Client':            p.presalesClientName || '',
      'Stage':             p.salesStage,
      'Progress':          presalesProgressLabel(p.progress),
      'Winning %':         p.winningPercentage,
      'Currency':          p.currency,
      'Revenue Target':    p.revenueTarget,
      'Weighted Value':    Math.round((p.revenueTarget ?? 0) * (p.winningPercentage ?? 0) / 100),
      'PM':                p.managerName || '',
      'Sales Owner':       p.accountManagerName || '',
      'Service Group':     p.serviceGroupName || '',
      'Cost Center':       p.costCenterName || '',
      'Lead Source':       p.leadSource || '',
      'Competitor':        p.competitor || '',
      'Start Date':        this.formatDate(p.startDate),
      'Expected Close':    this.formatDate(p.expectedCloseDate),
      'Actual Close':      this.formatDate(p.actualCloseDate),
      'Lost Reason':       p.lostReason || '',
      'Active':            p.isActive ? 'Yes' : 'No',
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook: XLSX.WorkBook = { Sheets: { 'Presales Inventory': worksheet }, SheetNames: ['Presales Inventory'] };
    const buffer: unknown = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer as ArrayBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `Presales_Inventory_${this.formatDateForFile(new Date())}.xlsx`);
  }

  private formatDateForFile(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}${m}${day}`;
  }

  // ================= Helpers =================

  private emptyProject(): PresalesProject {
    return {
      id: '',
      projectCode: '',
      name: '',
      managerId: '',
      managerName: '',
      accountManagerId: null,
      accountManagerName: '',
      presalesClientId: null,
      presalesClientName: '',
      serviceGroupId: null,
      serviceGroupName: '',
      costCenterId: null,
      costCenterName: '',
      revenueTarget: 0,
      currency: 'LKR',
      winningPercentage: 0,
      salesStage: 'Lead',
      progress: null,
      projectStatus: null,
      description: '',
      scopeSummary: '',
      comments: '',
      leadSource: '',
      competitor: '',
      lostReason: '',
      startDate: new Date().toISOString().substring(0, 10),
      endDate: null,
      expectedCloseDate: null,
      actualCloseDate: null,
      isActive: true,
      promotedToProjectId: null
    };
  }

  private toIsoDate(d: Date): string {
    // yyyy-MM-dd (no timezone quirks — treat as local calendar date)
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
